// CoursesHero.js - Courses-themed Hero section component
import { DEVICES } from '../../../constants/breakpoints';

/**
 * Creates a courses-themed Hero section with nested elements
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the hero is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The hero elements
 */
export const createCoursesHeroElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const heroSectionClass = 'courses-hero-section';
  const heroContainerClass = 'courses-hero-container';
  const heroContentClass = 'courses-hero-content';
  const heroTitleClass = 'courses-hero-title';
  const heroHighlightClass = 'courses-hero-highlight';
  const heroSubtitleClass = 'courses-hero-subtitle';
  const heroButtonsClass = 'courses-hero-buttons';
  const heroPrimaryBtnClass = 'courses-hero-primary-btn';
  const heroSecondaryBtnClass = 'courses-hero-secondary-btn';
  const heroImageWrapperClass = 'courses-hero-image-wrapper';
  const heroImageClass = 'courses-hero-image';
  const heroOverlayClass = 'courses-hero-overlay';
  const heroPatternOverlayClass = 'courses-hero-pattern-overlay';
  const heroGradientOverlayClass = 'courses-hero-gradient-overlay';
  const heroStatsCtnClass = 'courses-hero-stats-container';
  const heroStatClass = 'courses-hero-stat';
  const heroStatNumberClass = 'courses-hero-stat-number';
  const heroStatLabelClass = 'courses-hero-stat-label';
  const heroFeaturesClass = 'courses-hero-features';
  const heroFeatureClass = 'courses-hero-feature';
  const heroFeatureIconClass = 'courses-hero-feature-icon';
  const heroFeatureTextClass = 'courses-hero-feature-text';

  // Desktop Styles - Modern courses theme with vibrant gradients
  const heroSectionStyles = {
    position: 'relative',
    paddingTop: '100px',
    paddingBottom: '100px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#ffffff',
    background: `
      radial-gradient(circle at 20% 80%, rgba(99, 102, 241, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(139, 92, 246, 0.08) 0%, transparent 50%),
      linear-gradient(135deg, #ffffff 0%, #f8fafc 25%, #f1f5f9 50%, #f8fafc 75%, #ffffff 100%)
    `,
    color: '#1e293b',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    minHeight: '90vh'
  };

  const heroPatternOverlayStyles = {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    backgroundImage: `
      radial-gradient(circle at 25% 25%, rgba(99, 102, 241, 0.04) 1px, transparent 1px),
      radial-gradient(circle at 75% 75%, rgba(139, 92, 246, 0.04) 1px, transparent 1px)
    `,
    backgroundSize: '40px 40px, 60px 60px',
    backgroundPosition: '0 0, 30px 30px',
    zIndex: '0',
    opacity: '0.7'
  };

  const heroGradientOverlayStyles = {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    background: `
      linear-gradient(45deg, transparent 0%, rgba(99, 102, 241, 0.03) 25%, transparent 50%, rgba(139, 92, 246, 0.03) 75%, transparent 100%),
      conic-gradient(from 0deg at 50% 50%, transparent 0deg, rgba(99, 102, 241, 0.02) 90deg, transparent 180deg, rgba(139, 92, 246, 0.02) 270deg, transparent 360deg)
    `,
    zIndex: '0',
    opacity: '0.8'
  };

  const heroOverlayStyles = {
    position: 'absolute',
    top: '0',
    right: '0',
    width: '50%',
    height: '100%',
    background: `
      radial-gradient(ellipse at 70% 50%, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.04) 40%, transparent 70%),
      linear-gradient(135deg, transparent 0%, rgba(99, 102, 241, 0.05) 50%, transparent 100%)
    `,
    zIndex: '0'
  };

  const heroContainerStyles = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    maxWidth: '1400px',
    width: '100%',
    margin: '0 auto',
    gap: '80px',
    position: 'relative',
    zIndex: '1'
  };

  const heroContentStyles = {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    gap: '32px',
    maxWidth: '650px',
    position: 'relative',
    zIndex: '2'
  };

  const heroTitleStyles = {
    margin: '0',
    fontSize: '56px',
    fontWeight: '800',
    color: '#1e293b',
    lineHeight: '1.1',
    letterSpacing: '-0.02em',
    fontFamily: '"Inter", sans-serif'
  };

  const heroHighlightStyles = {
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    position: 'relative',
    display: 'inline-block',
    fontWeight: '900'
  };

  const heroSubtitleStyles = {
    margin: '0',
    fontSize: '20px',
    fontWeight: '400',
    color: '#64748b',
    lineHeight: '1.6',
    maxWidth: '580px',
    fontFamily: '"Inter", sans-serif'
  };

  const heroButtonsStyles = {
    display: 'flex',
    gap: '16px',
    marginTop: '16px',
    alignItems: 'center'
  };

  const heroPrimaryBtnStyles = {
    padding: '16px 32px',
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    color: '#ffffff',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontFamily: '"Inter", sans-serif',
    boxShadow: '0 8px 25px rgba(99, 102, 241, 0.3)',
    position: 'relative',
    overflow: 'hidden'
  };

  const heroSecondaryBtnStyles = {
    padding: '16px 32px',
    background: 'transparent',
    color: '#6366f1',
    border: '2px solid #6366f1',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontFamily: '"Inter", sans-serif',
    boxShadow: '0 4px 15px rgba(99, 102, 241, 0.1)'
  };

  const heroPrimaryBtnHoverStyles = {
    background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 35px rgba(99, 102, 241, 0.4)'
  };

  const heroSecondaryBtnHoverStyles = {
    background: '#6366f1',
    color: '#ffffff',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(99, 102, 241, 0.3)'
  };

  const heroImageWrapperStyles = {
    flex: '1',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    maxWidth: '600px',
    position: 'relative',
    zIndex: '2'
  };

  const heroImageStyles = {
    width: '100%',
    maxWidth: '550px',
    height: '600px',
    objectFit: 'cover',
    borderRadius: '20px',
    boxShadow: `
      0 25px 50px -12px rgba(0, 0, 0, 0.15),
      0 0 0 1px rgba(99, 102, 241, 0.1),
      0 0 30px rgba(99, 102, 241, 0.1)
    `,
    transition: 'all 0.6s ease',
    border: '1px solid rgba(226, 232, 240, 0.8)'
  };

  const heroFeaturesStyles = {
    display: 'flex',
    gap: '24px',
    marginTop: '32px',
    flexWrap: 'wrap'
  };

  const heroFeatureStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    backgroundColor: 'rgba(99, 102, 241, 0.05)',
    borderRadius: '8px',
    border: '1px solid rgba(99, 102, 241, 0.1)'
  };

  const heroFeatureIconStyles = {
    fontSize: '16px',
    color: '#6366f1'
  };

  const heroFeatureTextStyles = {
    fontSize: '14px',
    fontWeight: '500',
    color: '#475569',
    fontFamily: '"Inter", sans-serif'
  };

  const heroStatsCtnStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '48px',
    padding: '32px',
    background: `
      linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(139, 92, 246, 0.03) 100%),
      rgba(255, 255, 255, 0.8)
    `,
    borderRadius: '16px',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(226, 232, 240, 0.8)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)'
  };

  const heroStatStyles = {
    textAlign: 'center',
    padding: '0 20px'
  };

  const heroStatNumberStyles = {
    fontSize: '36px',
    fontWeight: '800',
    color: '#6366f1',
    marginBottom: '8px',
    fontFamily: '"Inter", sans-serif'
  };

  const heroStatLabelStyles = {
    fontSize: '14px',
    color: '#64748b',
    fontWeight: '500',
    fontFamily: '"Inter", sans-serif'
  };

  // Tablet Styles
  const tabletHeroContainerStyles = {
    ...heroContainerStyles,
    flexDirection: 'column',
    gap: '50px',
    padding: '0 30px'
  };

  const tabletHeroTitleStyles = {
    ...heroTitleStyles,
    fontSize: '48px',
    textAlign: 'center'
  };

  const tabletHeroSubtitleStyles = {
    ...heroSubtitleStyles,
    textAlign: 'center'
  };

  const tabletHeroButtonsStyles = {
    ...heroButtonsStyles,
    justifyContent: 'center'
  };

  const tabletHeroImageWrapperStyles = {
    ...heroImageWrapperStyles,
    maxWidth: '500px'
  };

  const tabletHeroImageStyles = {
    ...heroImageStyles,
    height: '500px'
  };

  const tabletHeroStatsCtnStyles = {
    ...heroStatsCtnStyles,
    padding: '24px'
  };

  const tabletHeroStatNumberStyles = {
    ...heroStatNumberStyles,
    fontSize: '32px'
  };

  const tabletHeroFeaturesStyles = {
    ...heroFeaturesStyles,
    justifyContent: 'center'
  };

  // Mobile Styles
  const mobileHeroSectionStyles = {
    ...heroSectionStyles,
    paddingTop: '80px',
    paddingBottom: '80px',
    minHeight: 'auto'
  };

  const mobileHeroContainerStyles = {
    ...heroContainerStyles,
    flexDirection: 'column',
    padding: '0 20px',
    gap: '40px'
  };

  const mobileHeroContentStyles = {
    ...heroContentStyles,
    alignItems: 'center',
    textAlign: 'center',
    maxWidth: '100%'
  };

  const mobileHeroTitleStyles = {
    ...heroTitleStyles,
    fontSize: '36px',
    textAlign: 'center'
  };

  const mobileHeroSubtitleStyles = {
    ...heroSubtitleStyles,
    fontSize: '18px',
    textAlign: 'center'
  };

  const mobileHeroButtonsStyles = {
    ...heroButtonsStyles,
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    width: '100%'
  };

  const mobileHeroPrimaryBtnStyles = {
    ...heroPrimaryBtnStyles,
    width: '100%',
    maxWidth: '280px'
  };

  const mobileHeroSecondaryBtnStyles = {
    ...heroSecondaryBtnStyles,
    width: '100%',
    maxWidth: '280px'
  };

  const mobileHeroImageWrapperStyles = {
    ...heroImageWrapperStyles,
    maxWidth: '100%',
    order: '-1'
  };

  const mobileHeroImageStyles = {
    ...heroImageStyles,
    height: '350px',
    maxWidth: '100%'
  };

  const mobileHeroStatsCtnStyles = {
    ...heroStatsCtnStyles,
    flexDirection: 'column',
    gap: '20px',
    padding: '24px'
  };

  const mobileHeroStatStyles = {
    ...heroStatStyles,
    padding: '12px 0'
  };

  const mobileHeroFeaturesStyles = {
    ...heroFeaturesStyles,
    justifyContent: 'center',
    gap: '12px'
  };

  const mobileHeroFeatureStyles = {
    ...heroFeatureStyles,
    padding: '6px 12px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(heroSectionClass, heroSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(heroContainerClass, heroContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(heroContentClass, heroContentStyles);
    newRegistry[DEVICES.DESKTOP].set(heroTitleClass, heroTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(heroHighlightClass, heroHighlightStyles);
    newRegistry[DEVICES.DESKTOP].set(heroSubtitleClass, heroSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(heroButtonsClass, heroButtonsStyles);
    newRegistry[DEVICES.DESKTOP].set(heroPrimaryBtnClass, heroPrimaryBtnStyles);
    newRegistry[DEVICES.DESKTOP].set(heroSecondaryBtnClass, heroSecondaryBtnStyles);
    newRegistry[DEVICES.DESKTOP].set(heroImageWrapperClass, heroImageWrapperStyles);
    newRegistry[DEVICES.DESKTOP].set(heroImageClass, heroImageStyles);
    newRegistry[DEVICES.DESKTOP].set(heroOverlayClass, heroOverlayStyles);
    newRegistry[DEVICES.DESKTOP].set(heroPatternOverlayClass, heroPatternOverlayStyles);
    newRegistry[DEVICES.DESKTOP].set(heroGradientOverlayClass, heroGradientOverlayStyles);
    newRegistry[DEVICES.DESKTOP].set(heroFeaturesClass, heroFeaturesStyles);
    newRegistry[DEVICES.DESKTOP].set(heroFeatureClass, heroFeatureStyles);
    newRegistry[DEVICES.DESKTOP].set(heroFeatureIconClass, heroFeatureIconStyles);
    newRegistry[DEVICES.DESKTOP].set(heroFeatureTextClass, heroFeatureTextStyles);
    newRegistry[DEVICES.DESKTOP].set(heroStatsCtnClass, heroStatsCtnStyles);
    newRegistry[DEVICES.DESKTOP].set(heroStatClass, heroStatStyles);
    newRegistry[DEVICES.DESKTOP].set(heroStatNumberClass, heroStatNumberStyles);
    newRegistry[DEVICES.DESKTOP].set(heroStatLabelClass, heroStatLabelStyles);

    // Hover styles
    newRegistry[DEVICES.DESKTOP].set(`${heroPrimaryBtnClass}--hover`, heroPrimaryBtnHoverStyles);
    newRegistry[DEVICES.DESKTOP].set(`${heroSecondaryBtnClass}--hover`, heroSecondaryBtnHoverStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(heroContainerClass, tabletHeroContainerStyles);
    newRegistry[DEVICES.TABLET].set(heroTitleClass, tabletHeroTitleStyles);
    newRegistry[DEVICES.TABLET].set(heroSubtitleClass, tabletHeroSubtitleStyles);
    newRegistry[DEVICES.TABLET].set(heroButtonsClass, tabletHeroButtonsStyles);
    newRegistry[DEVICES.TABLET].set(heroImageWrapperClass, tabletHeroImageWrapperStyles);
    newRegistry[DEVICES.TABLET].set(heroImageClass, tabletHeroImageStyles);
    newRegistry[DEVICES.TABLET].set(heroStatsCtnClass, tabletHeroStatsCtnStyles);
    newRegistry[DEVICES.TABLET].set(heroStatNumberClass, tabletHeroStatNumberStyles);
    newRegistry[DEVICES.TABLET].set(heroFeaturesClass, tabletHeroFeaturesStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(heroSectionClass, mobileHeroSectionStyles);
    newRegistry[DEVICES.MOBILE].set(heroContainerClass, mobileHeroContainerStyles);
    newRegistry[DEVICES.MOBILE].set(heroContentClass, mobileHeroContentStyles);
    newRegistry[DEVICES.MOBILE].set(heroTitleClass, mobileHeroTitleStyles);
    newRegistry[DEVICES.MOBILE].set(heroSubtitleClass, mobileHeroSubtitleStyles);
    newRegistry[DEVICES.MOBILE].set(heroButtonsClass, mobileHeroButtonsStyles);
    newRegistry[DEVICES.MOBILE].set(heroPrimaryBtnClass, mobileHeroPrimaryBtnStyles);
    newRegistry[DEVICES.MOBILE].set(heroSecondaryBtnClass, mobileHeroSecondaryBtnStyles);
    newRegistry[DEVICES.MOBILE].set(heroImageWrapperClass, mobileHeroImageWrapperStyles);
    newRegistry[DEVICES.MOBILE].set(heroImageClass, mobileHeroImageStyles);
    newRegistry[DEVICES.MOBILE].set(heroStatsCtnClass, mobileHeroStatsCtnStyles);
    newRegistry[DEVICES.MOBILE].set(heroStatClass, mobileHeroStatStyles);
    newRegistry[DEVICES.MOBILE].set(heroFeaturesClass, mobileHeroFeaturesStyles);
    newRegistry[DEVICES.MOBILE].set(heroFeatureClass, mobileHeroFeatureStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    heroSectionClass,
    heroContainerClass,
    heroContentClass,
    heroTitleClass,
    heroHighlightClass,
    heroSubtitleClass,
    heroButtonsClass,
    heroPrimaryBtnClass,
    heroSecondaryBtnClass,
    heroImageWrapperClass,
    heroImageClass,
    heroOverlayClass,
    heroPatternOverlayClass,
    heroGradientOverlayClass,
    heroFeaturesClass,
    heroFeatureClass,
    heroFeatureIconClass,
    heroFeatureTextClass,
    heroStatsCtnClass,
    heroStatClass,
    heroStatNumberClass,
    heroStatLabelClass
  ]));

  // Generate IDs for all elements
  const heroSectionId = Date.now();
  const containerID = Date.now() + 1;
  const contentID = Date.now() + 2;
  const titleID = Date.now() + 3;
  const highlightTextID = Date.now() + 4;
  const subtitleID = Date.now() + 5;
  const buttonsWrapperID = Date.now() + 6;
  const primaryButtonID = Date.now() + 7;
  const secondaryButtonID = Date.now() + 8;
  const featuresWrapperID = Date.now() + 9;
  const feature1ID = Date.now() + 10;
  const feature1IconID = Date.now() + 11;
  const feature1TextID = Date.now() + 12;
  const feature2ID = Date.now() + 13;
  const feature2IconID = Date.now() + 14;
  const feature2TextID = Date.now() + 15;
  const feature3ID = Date.now() + 16;
  const feature3IconID = Date.now() + 17;
  const feature3TextID = Date.now() + 18;
  const imageWrapperID = Date.now() + 19;
  const heroImageID = Date.now() + 20;
  const overlayID = Date.now() + 21;
  const patternOverlayID = Date.now() + 22;
  const gradientOverlayID = Date.now() + 23;
  const statsContainerID = Date.now() + 24;
  const stat1ID = Date.now() + 25;
  const stat1NumberID = Date.now() + 26;
  const stat1LabelID = Date.now() + 27;
  const stat2ID = Date.now() + 28;
  const stat2NumberID = Date.now() + 29;
  const stat2LabelID = Date.now() + 30;
  const stat3ID = Date.now() + 31;
  const stat3NumberID = Date.now() + 32;
  const stat3LabelID = Date.now() + 33;

  const newElements = [
    // Main Hero Section Container
    {
      id: heroSectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Courses Hero Section',
      parentId: null,
      className: heroSectionClass,
      styles: {},
      children: [patternOverlayID, gradientOverlayID, overlayID, containerID],
      text: '',
      attributes: {
        'data-element': 'courses-hero-section',
        'data-component': 'section',
        'data-section-type': 'hero',
        'data-page-id': currentPageId
      }
    },
    
    // Pattern Overlay
    {
      id: patternOverlayID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pattern Overlay',
      parentId: heroSectionId,
      hierarchyName: "Pattern Overlay",
      className: heroPatternOverlayClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'courses-hero-pattern-overlay'
      }
    },
    
    // Gradient Overlay
    {
      id: gradientOverlayID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Gradient Overlay',
      parentId: heroSectionId,
      hierarchyName: "Gradient Overlay",
      className: heroGradientOverlayClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'courses-hero-gradient-overlay'
      }
    },
    
    // Background Overlay
    {
      id: overlayID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Background Overlay',
      parentId: heroSectionId,
      hierarchyName: "Background Overlay",
      className: heroOverlayClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'courses-hero-overlay'
      }
    },
    
    // Container for content and image
    {
      id: containerID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Hero Container',
      parentId: heroSectionId,
      hierarchyName: "Hero Container",
      className: heroContainerClass,
      styles: {},
      children: [contentID, imageWrapperID],
      text: '',
      attributes: {
        'data-element': 'courses-hero-container'
      }
    },
    
    // Content area (left side)
    {
      id: contentID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Hero Content',
      parentId: containerID,
      hierarchyName: "Hero Content",
      className: heroContentClass,
      styles: {},
      children: [titleID, subtitleID, featuresWrapperID, buttonsWrapperID, statsContainerID],
      text: '',
      attributes: {
        'data-element': 'courses-hero-content'
      }
    },
    
    // Title
    {
      id: titleID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h1',
      name: 'Hero Title',
      parentId: contentID,
      hierarchyName: "Hero Title",
      className: heroTitleClass,
      styles: {},
      children: [highlightTextID],
      text: 'Master new skills with ',
      attributes: {
        'data-element': 'courses-hero-title'
      }
    },
    
    // Highlight Text Span
    {
      id: highlightTextID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Highlight Text',
      parentId: titleID,
      hierarchyName: "Highlight Text",
      className: heroHighlightClass,
      styles: {},
      children: [],
      text: 'expert-led courses',
      attributes: {
        'data-element': 'courses-hero-highlight-text'
      }
    },
    
    // Subtitle
    {
      id: subtitleID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Hero Subtitle',
      parentId: contentID,
      hierarchyName: "Hero Subtitle",
      className: heroSubtitleClass,
      styles: {},
      children: [],
      text: 'Join thousands of learners advancing their careers with our comprehensive online courses. Learn at your own pace with lifetime access and expert support.',
      attributes: {
        'data-element': 'courses-hero-subtitle'
      }
    },

    // Features Wrapper
    {
      id: featuresWrapperID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Hero Features',
      parentId: contentID,
      hierarchyName: "Hero Features",
      className: heroFeaturesClass,
      styles: {},
      children: [feature1ID, feature2ID, feature3ID],
      text: '',
      attributes: {
        'data-element': 'courses-hero-features'
      }
    },

    // Feature 1
    {
      id: feature1ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Feature 1',
      parentId: featuresWrapperID,
      hierarchyName: "Feature 1",
      className: heroFeatureClass,
      styles: {},
      children: [feature1IconID, feature1TextID],
      text: '',
      attributes: {
        'data-element': 'courses-hero-feature'
      }
    },

    // Feature 1 Icon
    {
      id: feature1IconID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Feature 1 Icon',
      parentId: feature1ID,
      hierarchyName: "Feature 1 Icon",
      className: heroFeatureIconClass,
      styles: {},
      children: [],
      text: '✓',
      attributes: {
        'data-element': 'courses-hero-feature-icon'
      }
    },

    // Feature 1 Text
    {
      id: feature1TextID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Feature 1 Text',
      parentId: feature1ID,
      hierarchyName: "Feature 1 Text",
      className: heroFeatureTextClass,
      styles: {},
      children: [],
      text: 'Lifetime Access',
      attributes: {
        'data-element': 'courses-hero-feature-text'
      }
    },

    // Feature 2
    {
      id: feature2ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Feature 2',
      parentId: featuresWrapperID,
      hierarchyName: "Feature 2",
      className: heroFeatureClass,
      styles: {},
      children: [feature2IconID, feature2TextID],
      text: '',
      attributes: {
        'data-element': 'courses-hero-feature'
      }
    },

    // Feature 2 Icon
    {
      id: feature2IconID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Feature 2 Icon',
      parentId: feature2ID,
      hierarchyName: "Feature 2 Icon",
      className: heroFeatureIconClass,
      styles: {},
      children: [],
      text: '✓',
      attributes: {
        'data-element': 'courses-hero-feature-icon'
      }
    },

    // Feature 2 Text
    {
      id: feature2TextID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Feature 2 Text',
      parentId: feature2ID,
      hierarchyName: "Feature 2 Text",
      className: heroFeatureTextClass,
      styles: {},
      children: [],
      text: 'Expert Instructors',
      attributes: {
        'data-element': 'courses-hero-feature-text'
      }
    },

    // Feature 3
    {
      id: feature3ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Feature 3',
      parentId: featuresWrapperID,
      hierarchyName: "Feature 3",
      className: heroFeatureClass,
      styles: {},
      children: [feature3IconID, feature3TextID],
      text: '',
      attributes: {
        'data-element': 'courses-hero-feature'
      }
    },

    // Feature 3 Icon
    {
      id: feature3IconID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Feature 3 Icon',
      parentId: feature3ID,
      hierarchyName: "Feature 3 Icon",
      className: heroFeatureIconClass,
      styles: {},
      children: [],
      text: '✓',
      attributes: {
        'data-element': 'courses-hero-feature-icon'
      }
    },

    // Feature 3 Text
    {
      id: feature3TextID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Feature 3 Text',
      parentId: feature3ID,
      hierarchyName: "Feature 3 Text",
      className: heroFeatureTextClass,
      styles: {},
      children: [],
      text: 'Certificate',
      attributes: {
        'data-element': 'courses-hero-feature-text'
      }
    },
    
    // Buttons Wrapper
    {
      id: buttonsWrapperID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Hero Buttons',
      parentId: contentID,
      hierarchyName: "Hero Buttons",
      className: heroButtonsClass,
      styles: {},
      children: [primaryButtonID, secondaryButtonID],
      text: '',
      attributes: {
        'data-element': 'courses-hero-buttons'
      }
    },
    
    // Primary Button
    {
      id: primaryButtonID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Primary Button',
      parentId: buttonsWrapperID,
      hierarchyName: "Primary Button",
      className: heroPrimaryBtnClass,
      styles: {},
      children: [],
      text: 'Start Learning',
      attributes: {
        'data-element': 'courses-primary-button'
      },
      stateClassName: {
        hover: '',
        pressed: '',
        focused: ''
      },
      currentState: 'default',
      linkTo: 'enrollment'
    },
    
    // Secondary Button
    {
      id: secondaryButtonID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Secondary Button',
      parentId: buttonsWrapperID,
      hierarchyName: "Secondary Button",
      className: heroSecondaryBtnClass,
      styles: {},
      children: [],
      text: 'Browse Courses',
      attributes: {
        'data-element': 'courses-secondary-button'
      },
      stateClassName: {
        hover: '',
        pressed: '',
        focused: ''
      },
      currentState: 'default',
      linkTo: 'enrollment'
    },
    
    // Stats Container
    {
      id: statsContainerID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Stats Container',
      parentId: contentID,
      hierarchyName: "Stats Container",
      className: heroStatsCtnClass,
      styles: {},
      children: [stat1ID, stat2ID, stat3ID],
      text: '',
      attributes: {
        'data-element': 'courses-hero-stats-container'
      }
    },
    
    // Stat 1
    {
      id: stat1ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Stat 1',
      parentId: statsContainerID,
      hierarchyName: "Stat 1",
      className: heroStatClass,
      styles: {},
      children: [stat1NumberID, stat1LabelID],
      text: '',
      attributes: {
        'data-element': 'courses-hero-stat'
      }
    },
    
    // Stat 1 Number
    {
      id: stat1NumberID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Stat 1 Number',
      parentId: stat1ID,
      hierarchyName: "Stat 1 Number",
      className: heroStatNumberClass,
      styles: {},
      children: [],
      text: '50K+',
      attributes: {
        'data-element': 'courses-hero-stat-number'
      }
    },
    
    // Stat 1 Label
    {
      id: stat1LabelID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Stat 1 Label',
      parentId: stat1ID,
      hierarchyName: "Stat 1 Label",
      className: heroStatLabelClass,
      styles: {},
      children: [],
      text: 'Students Enrolled',
      attributes: {
        'data-element': 'courses-hero-stat-label'
      }
    },
    
    // Stat 2
    {
      id: stat2ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Stat 2',
      parentId: statsContainerID,
      hierarchyName: "Stat 2",
      className: heroStatClass,
      styles: {},
      children: [stat2NumberID, stat2LabelID],
      text: '',
      attributes: {
        'data-element': 'courses-hero-stat'
      }
    },
    
    // Stat 2 Number
    {
      id: stat2NumberID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Stat 2 Number',
      parentId: stat2ID,
      hierarchyName: "Stat 2 Number",
      className: heroStatNumberClass,
      styles: {},
      children: [],
      text: '200+',
      attributes: {
        'data-element': 'courses-hero-stat-number'
      }
    },
    
    // Stat 2 Label
    {
      id: stat2LabelID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Stat 2 Label',
      parentId: stat2ID,
      hierarchyName: "Stat 2 Label",
      className: heroStatLabelClass,
      styles: {},
      children: [],
      text: 'Expert Courses',
      attributes: {
        'data-element': 'courses-hero-stat-label'
      }
    },
    
    // Stat 3
    {
      id: stat3ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Stat 3',
      parentId: statsContainerID,
      hierarchyName: "Stat 3",
      className: heroStatClass,
      styles: {},
      children: [stat3NumberID, stat3LabelID],
      text: '',
      attributes: {
        'data-element': 'courses-hero-stat'
      }
    },
    
    // Stat 3 Number
    {
      id: stat3NumberID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Stat 3 Number',
      parentId: stat3ID,
      hierarchyName: "Stat 3 Number",
      className: heroStatNumberClass,
      styles: {},
      children: [],
      text: '95%',
      attributes: {
        'data-element': 'courses-hero-stat-number'
      }
    },
    
    // Stat 3 Label
    {
      id: stat3LabelID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Stat 3 Label',
      parentId: stat3ID,
      hierarchyName: "Stat 3 Label",
      className: heroStatLabelClass,
      styles: {},
      children: [],
      text: 'Success Rate',
      attributes: {
        'data-element': 'courses-hero-stat-label'
      }
    },
    
    // Image Wrapper (right side)
    {
      id: imageWrapperID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Hero Image Wrapper',
      parentId: containerID,
      hierarchyName: "Hero Image Wrapper",
      className: heroImageWrapperClass,
      styles: {},
      children: [heroImageID],
      text: '',
      attributes: {
        'data-element': 'courses-hero-image-wrapper'
      }
    },
    
    // Hero Image
    {
      id: heroImageID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Hero Image',
      parentId: imageWrapperID,
      hierarchyName: "Hero Image",
      className: heroImageClass,
      styles: {},
      children: [],
      text: '',
      src: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?q=80&w=2071&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      attributes: {
        'data-element': 'courses-hero-image',
        'alt': 'Students learning online courses'
      }
    }
  ];
  
  // Return all elements
  return {
    heroSection: newElements[0],
    elements: newElements
  };
}; 